/**
 * actionLogger.js — Standalone action logging utility
 *
 * Writes entries directly to the action_log table via the Express API.
 * This is a plain function (not a hook) so it can be called from
 * mutation onSuccess callbacks, event handlers, or anywhere else.
 *
 * Fire-and-forget: logging errors are caught and logged to console
 * but never block the calling operation.
 *
 * DB schema:
 *   id          text PK
 *   action      text        — e.g. "Create Job"
 *   category    text        — e.g. "create", "update", "delete", "login"
 *   description text        — Human-readable summary
 *   data        jsonb       — Extra context (user, jobId, jobTitle, etc.)
 *   created_at  timestamptz — Auto-filled by DB
 */

import { api } from './api';
import { uid } from './utils';
import { queryClient, queryKeys } from './queryClient';

/**
 * Derive a category from the action string for filtering in the UI.
 */
function deriveCategory(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('create') || a.includes('add')) return 'create';
  if (a.includes('update') || a.includes('edit') || a.includes('change')) return 'update';
  if (a.includes('delete') || a.includes('remove')) return 'delete';
  if (a.includes('login') || a.includes('signin')) return 'login';
  if (a.includes('logout') || a.includes('signout')) return 'logout';
  if (a.includes('export') || a.includes('download')) return 'export';
  if (a.includes('import') || a.includes('upload')) return 'import';
  return 'other';
}

/**
 * Log an action to the action_log table.
 *
 * @param {Object} entry
 * @param {string} entry.action       - e.g. "Create Job", "Update Invoice"
 * @param {string} [entry.user]       - Display name/email (auto-filled from session)
 * @param {string} [entry.jobId]      - Related job ID
 * @param {string} [entry.jobTitle]   - Related job title
 * @param {string} [entry.description] - Human-readable summary
 * @param {object} [entry.details]    - Additional context (packed into data jsonb)
 */
export async function logAction(entry) {
  try {
    const user = entry.user || 'system';

    // Build description from action + context
    const description = entry.description ||
      [entry.action, entry.jobTitle ? `— ${entry.jobTitle}` : ''].filter(Boolean).join(' ');

    const row = {
      id: uid(),
      action: entry.action,
      category: deriveCategory(entry.action),
      description,
      user,
      ...(entry.jobId ? { job_id: entry.jobId } : {}),
      ...(entry.jobTitle ? { job_title: entry.jobTitle } : {}),
      ...(entry.details && Object.keys(entry.details).length ? { data: entry.details } : {}),
    };

    await api.post('/action-log', row);
    queryClient.invalidateQueries({ queryKey: queryKeys.actionLog.all });
  } catch (err) {
    // Never let logging failures propagate
    console.error('[ActionLogger] Error:', err.message);
  }
}
